/**
 * VA C&P-aligned symptom taxonomy.
 *
 * Curated from VA Disability Benefits Questionnaires (DBQs), VBMS examiner
 * dropdowns, and 38 CFR Part 4 rating criteria language. Each symptom has a
 * stable `code`, a `label` the way a C&P examiner would write it, the
 * `bodySystem` it rolls up to, and (where relevant) a `bodyRegion` that
 * pre-fills the location field. Use these exact terms in the picker so the
 * generated PDF reads like clinical evidence, not like patient narrative.
 *
 * Format:
 *   { code, label, bodySystem, bodyRegion?, painLike? }
 *
 * Public surface:
 *   - BODY_SYSTEMS: ordered list of system ids + display labels
 *   - SYMPTOM_CATALOG: readonly array of entries
 *   - findSymptomMatches(query, limit?)
 *   - getSymptomByCode(code)
 *   - getSymptomsBySystem(systemId)
 */

export const BODY_SYSTEMS = [
  { id: "msk", label: "Musculoskeletal" },
  { id: "mental", label: "Mental Health" },
  { id: "neuro", label: "Neurological" },
  { id: "audio", label: "Hearing / Tinnitus" },
  { id: "ent", label: "ENT" },
  { id: "eye", label: "Eyes" },
  { id: "cardio", label: "Cardiovascular" },
  { id: "pulm", label: "Respiratory" },
  { id: "gi", label: "Gastrointestinal" },
  { id: "gu", label: "Genitourinary" },
  { id: "skin", label: "Skin" },
  { id: "endo", label: "Endocrine / Metabolic" },
  { id: "heme", label: "Hematological / Immune" },
];

// Compact entry builder (kept local for readability — keeps the array short).
const s = (code, label, bodySystem, extra = {}) => ({ code, label, bodySystem, ...extra });

export const SYMPTOM_CATALOG = [
  // ---------- Musculoskeletal ----------
  s("msk.painful-motion", "Painful motion", "msk", { painLike: true }),
  s("msk.limited-rom", "Limited range of motion", "msk"),
  s("msk.flare-up", "Functional flare-up", "msk"),
  s("msk.stiffness-am", "Morning stiffness", "msk"),
  s("msk.crepitus", "Crepitus (joint clicking / grinding)", "msk"),
  s("msk.guarding", "Guarding of movement", "msk"),
  s("msk.tenderness", "Tenderness on palpation", "msk"),
  s("msk.muscle-spasm", "Muscle spasm", "msk"),
  s("msk.muscle-atrophy", "Muscle atrophy", "msk"),
  s("msk.muscle-weakness", "Reduced muscle strength", "msk"),
  s("msk.swelling", "Joint swelling / effusion", "msk"),
  s("msk.deformity", "Joint deformity", "msk"),
  s("msk.gait-antalgic", "Antalgic gait", "msk"),
  s("msk.gait-shuffling", "Shuffling gait", "msk"),
  s("msk.brace-use", "Brace / assistive device use", "msk"),
  s("msk.cane-use", "Cane / walker use", "msk"),
  s("msk.instability", "Instability or giving-way", "msk"),
  s("msk.locking", "Locking of joint", "msk"),
  s("msk.popping", "Audible popping", "msk"),
  s("msk.numbness-radicular", "Radicular numbness", "msk"),

  // Lumbar / Lower back
  s("msk.lumbar-pain", "Lumbar (low back) pain", "msk", { bodyRegion: "Lumbar spine", painLike: true }),
  s("msk.lumbar-radiculopathy", "Lumbar radiculopathy (sciatica)", "msk", { bodyRegion: "Lumbar spine / lower extremity", painLike: true }),
  s("msk.thoracic-pain", "Thoracic (mid back) pain", "msk", { bodyRegion: "Thoracic spine", painLike: true }),
  s("msk.cervical-pain", "Cervical (neck) pain", "msk", { bodyRegion: "Cervical spine", painLike: true }),
  s("msk.cervical-headache", "Cervicogenic headache", "msk", { bodyRegion: "Cervical spine / head" }),

  // Shoulder
  s("msk.shoulder-pain", "Shoulder pain", "msk", { bodyRegion: "Shoulder", painLike: true }),
  s("msk.shoulder-impingement", "Shoulder impingement sign", "msk", { bodyRegion: "Shoulder" }),
  s("msk.shoulder-rotation-loss", "Loss of shoulder rotation", "msk", { bodyRegion: "Shoulder" }),

  // Elbow / Wrist / Hand
  s("msk.elbow-pain", "Elbow pain", "msk", { bodyRegion: "Elbow", painLike: true }),
  s("msk.wrist-pain", "Wrist pain", "msk", { bodyRegion: "Wrist", painLike: true }),
  s("msk.hand-grip-loss", "Reduced grip strength", "msk", { bodyRegion: "Hand" }),
  s("msk.finger-tingling", "Finger tingling / paresthesia", "msk", { bodyRegion: "Hand / fingers" }),

  // Hip / Knee / Ankle / Foot
  s("msk.hip-pain", "Hip pain", "msk", { bodyRegion: "Hip", painLike: true }),
  s("msk.knee-pain", "Knee pain", "msk", { bodyRegion: "Knee", painLike: true }),
  s("msk.knee-instability", "Knee instability / buckling", "msk", { bodyRegion: "Knee" }),
  s("msk.patellofemoral", "Patellofemoral pain (anterior knee)", "msk", { bodyRegion: "Knee", painLike: true }),
  s("msk.ankle-pain", "Ankle pain", "msk", { bodyRegion: "Ankle", painLike: true }),
  s("msk.ankle-instability", "Ankle instability", "msk", { bodyRegion: "Ankle" }),
  s("msk.foot-pain", "Foot pain", "msk", { bodyRegion: "Foot", painLike: true }),
  s("msk.plantar-fasciitis", "Plantar fasciitis (heel pain)", "msk", { bodyRegion: "Foot / heel", painLike: true }),
  s("msk.pes-planus", "Pes planus (flat feet)", "msk", { bodyRegion: "Foot" }),

  // ---------- Mental Health ----------
  s("mh.depressed-mood", "Depressed mood", "mental"),
  s("mh.anhedonia", "Anhedonia (loss of interest)", "mental"),
  s("mh.anxiety", "Generalized anxiety", "mental"),
  s("mh.panic-attack", "Panic attack", "mental"),
  s("mh.intrusive-memories", "Intrusive recollections of trauma", "mental"),
  s("mh.flashbacks", "Flashbacks / dissociation", "mental"),
  s("mh.nightmares", "Trauma-related nightmares", "mental"),
  s("mh.avoidance", "Avoidance of trauma triggers", "mental"),
  s("mh.hypervigilance", "Hypervigilance", "mental"),
  s("mh.exaggerated-startle", "Exaggerated startle response", "mental"),
  s("mh.irritability", "Irritability / angry outbursts", "mental"),
  s("mh.concentration-loss", "Difficulty concentrating", "mental"),
  s("mh.memory-loss", "Short-term memory loss", "mental"),
  s("mh.sleep-disturbance", "Sleep disturbance", "mental"),
  s("mh.early-waking", "Early-morning waking", "mental"),
  s("mh.insomnia", "Initial insomnia (trouble falling asleep)", "mental"),
  s("mh.mid-insomnia", "Middle insomnia (waking through the night)", "mental"),
  s("mh.fatigue-mental", "Mental fatigue", "mental"),
  s("mh.guilt", "Feelings of guilt or worthlessness", "mental"),
  s("mh.suicidal-ideation", "Suicidal ideation (passive)", "mental"),
  s("mh.suicidal-plan", "Suicidal ideation with plan", "mental"),
  s("mh.social-withdrawal", "Social withdrawal / isolation", "mental"),
  s("mh.detachment", "Feelings of detachment from others", "mental"),
  s("mh.negative-self", "Persistent negative beliefs about self", "mental"),
  s("mh.hopelessness", "Hopelessness about the future", "mental"),
  s("mh.crowd-avoidance", "Avoidance of crowded places", "mental"),
  s("mh.relationship-strain", "Strained personal relationships", "mental"),
  s("mh.work-impairment", "Occupational / work impairment", "mental"),
  s("mh.alcohol-use", "Increased alcohol use to cope", "mental"),
  s("mh.derealization", "Derealization / unreality feeling", "mental"),
  s("mh.tearfulness", "Episodes of unexplained tearfulness", "mental"),
  s("mh.appetite-decrease", "Decreased appetite", "mental"),
  s("mh.appetite-increase", "Increased appetite / emotional eating", "mental"),

  // ---------- Neurological ----------
  s("neuro.headache-migraine", "Migraine headache (with aura)", "neuro", { bodyRegion: "Head", painLike: true }),
  s("neuro.headache-tension", "Tension-type headache", "neuro", { bodyRegion: "Head", painLike: true }),
  s("neuro.headache-prostrating", "Prostrating headache (must lie down)", "neuro", { bodyRegion: "Head" }),
  s("neuro.dizziness", "Dizziness / lightheadedness", "neuro"),
  s("neuro.vertigo", "Vertigo (room spinning)", "neuro"),
  s("neuro.balance-loss", "Loss of balance", "neuro"),
  s("neuro.numbness-extremity", "Numbness of extremity", "neuro"),
  s("neuro.tingling", "Tingling / paresthesia", "neuro"),
  s("neuro.burning-sensation", "Burning sensation (neuropathic)", "neuro"),
  s("neuro.tremor-resting", "Resting tremor", "neuro"),
  s("neuro.tremor-action", "Action / intention tremor", "neuro"),
  s("neuro.muscle-cramp", "Muscle cramps", "neuro"),
  s("neuro.foot-drop", "Foot drop", "neuro"),
  s("neuro.cognitive-fog", "Cognitive fog / slowed thinking", "neuro"),
  s("neuro.word-finding", "Word-finding difficulty", "neuro"),
  s("neuro.seizure", "Seizure activity", "neuro"),
  s("neuro.syncope", "Syncope (fainting)", "neuro"),
  s("neuro.photophobia", "Photophobia (light sensitivity)", "neuro"),
  s("neuro.phonophobia", "Phonophobia (sound sensitivity)", "neuro"),
  s("neuro.aura", "Visual aura", "neuro"),

  // ---------- Hearing / Tinnitus ----------
  s("aud.tinnitus-ringing", "Tinnitus — ringing", "audio", { bodyRegion: "Ears" }),
  s("aud.tinnitus-buzzing", "Tinnitus — buzzing / humming", "audio", { bodyRegion: "Ears" }),
  s("aud.hearing-loss-high", "Hearing loss — high frequencies", "audio", { bodyRegion: "Ears" }),
  s("aud.hearing-loss-conv", "Difficulty understanding conversation", "audio", { bodyRegion: "Ears" }),
  s("aud.hearing-loss-noise", "Difficulty hearing in background noise", "audio", { bodyRegion: "Ears" }),
  s("aud.aural-fullness", "Aural fullness / pressure", "audio", { bodyRegion: "Ears" }),
  s("aud.hyperacusis", "Hyperacusis (sound intolerance)", "audio", { bodyRegion: "Ears" }),

  // ---------- ENT ----------
  s("ent.nasal-congestion", "Chronic nasal congestion", "ent"),
  s("ent.rhinorrhea", "Persistent rhinorrhea (runny nose)", "ent"),
  s("ent.postnasal-drip", "Postnasal drip", "ent"),
  s("ent.sinus-pressure", "Sinus pressure / pain", "ent", { painLike: true }),
  s("ent.sneezing-attacks", "Repeated sneezing attacks", "ent"),
  s("ent.sore-throat-chronic", "Chronic sore throat", "ent"),
  s("ent.hoarseness", "Hoarseness", "ent"),
  s("ent.cough-chronic", "Chronic cough", "ent"),
  s("ent.smell-loss", "Anosmia (loss of smell)", "ent"),
  s("ent.taste-loss", "Ageusia (loss of taste)", "ent"),
  s("ent.dry-mouth", "Persistent dry mouth", "ent"),
  s("ent.tmj-pain", "TMJ pain / jaw clicking", "ent", { bodyRegion: "Jaw", painLike: true }),

  // ---------- Eyes ----------
  s("eye.blurred-vision", "Blurred vision", "eye"),
  s("eye.double-vision", "Double vision (diplopia)", "eye"),
  s("eye.dry-eyes", "Dry eyes", "eye"),
  s("eye.tearing", "Excessive tearing", "eye"),
  s("eye.eye-pain", "Eye pain", "eye", { bodyRegion: "Eyes", painLike: true }),
  s("eye.floaters", "New or increasing floaters", "eye"),
  s("eye.flashes", "Flashes of light", "eye"),
  s("eye.eye-strain", "Eye strain on near work", "eye"),
  s("eye.light-sensitivity", "Photophobia (eye)", "eye"),
  s("eye.peripheral-loss", "Peripheral vision loss", "eye"),

  // ---------- Cardiovascular ----------
  s("cv.chest-pain", "Chest pain", "cardio", { painLike: true }),
  s("cv.chest-tightness", "Chest tightness / pressure", "cardio"),
  s("cv.palpitations", "Palpitations", "cardio"),
  s("cv.dyspnea-exertion", "Dyspnea on exertion", "cardio"),
  s("cv.dyspnea-rest", "Dyspnea at rest", "cardio"),
  s("cv.orthopnea", "Orthopnea (shortness of breath lying flat)", "cardio"),
  s("cv.lower-edema", "Lower-extremity edema", "cardio"),
  s("cv.claudication", "Calf pain on walking (claudication)", "cardio"),
  s("cv.cold-extremities", "Cold extremities", "cardio"),
  s("cv.high-bp", "Elevated blood pressure reading", "cardio"),
  s("cv.low-bp", "Low blood pressure / lightheadedness", "cardio"),

  // ---------- Respiratory ----------
  s("pulm.shortness-breath", "Shortness of breath", "pulm"),
  s("pulm.wheezing", "Wheezing", "pulm"),
  s("pulm.cough-productive", "Productive (wet) cough", "pulm"),
  s("pulm.cough-dry", "Dry cough", "pulm"),
  s("pulm.sputum", "Increased sputum production", "pulm"),
  s("pulm.hemoptysis", "Blood-tinged sputum (hemoptysis)", "pulm"),
  s("pulm.inhaler-use", "Inhaler use in past 24 h", "pulm"),
  s("pulm.exercise-limit", "Limited exercise tolerance", "pulm"),
  s("pulm.chest-tightness", "Chest tightness (respiratory)", "pulm"),
  s("pulm.snoring", "Heavy snoring / witnessed apnea", "pulm"),

  // ---------- Gastrointestinal ----------
  s("gi.epigastric-pain", "Epigastric (upper-abdominal) pain", "gi", { bodyRegion: "Abdomen", painLike: true }),
  s("gi.abdominal-pain", "Generalized abdominal pain", "gi", { bodyRegion: "Abdomen", painLike: true }),
  s("gi.nausea", "Nausea", "gi"),
  s("gi.vomiting", "Vomiting", "gi"),
  s("gi.heartburn", "Heartburn / acid reflux", "gi"),
  s("gi.regurgitation", "Regurgitation of food", "gi"),
  s("gi.early-satiety", "Early satiety", "gi"),
  s("gi.bloating", "Abdominal bloating / distension", "gi"),
  s("gi.diarrhea", "Diarrhea", "gi"),
  s("gi.constipation", "Constipation", "gi"),
  s("gi.urgency-bm", "Bowel urgency", "gi"),
  s("gi.incontinence-bm", "Fecal incontinence", "gi"),
  s("gi.blood-stool", "Blood in stool", "gi"),
  s("gi.appetite-loss", "Loss of appetite", "gi"),
  s("gi.weight-loss-unintended", "Unintended weight loss", "gi"),

  // ---------- Genitourinary ----------
  s("gu.urinary-frequency", "Urinary frequency", "gu"),
  s("gu.urinary-urgency", "Urinary urgency", "gu"),
  s("gu.nocturia", "Nocturia (waking to urinate)", "gu"),
  s("gu.incontinence", "Urinary incontinence", "gu"),
  s("gu.dysuria", "Dysuria (painful urination)", "gu", { painLike: true }),
  s("gu.weak-stream", "Weak urinary stream", "gu"),
  s("gu.retention", "Urinary retention", "gu"),
  s("gu.hematuria", "Blood in urine", "gu"),
  s("gu.erectile-dysfunction", "Erectile dysfunction", "gu"),
  s("gu.pelvic-pain", "Pelvic pain", "gu", { bodyRegion: "Pelvis", painLike: true }),
  s("gu.testicular-pain", "Testicular pain", "gu", { painLike: true }),

  // ---------- Skin ----------
  s("skin.rash-erythema", "Erythematous rash", "skin"),
  s("skin.rash-papular", "Papular / pustular rash", "skin"),
  s("skin.pruritus", "Pruritus (itching)", "skin"),
  s("skin.scaling", "Scaling / flaking skin", "skin"),
  s("skin.ulcer", "Skin ulcer / non-healing wound", "skin"),
  s("skin.hyperhidrosis", "Excessive sweating (hyperhidrosis)", "skin"),
  s("skin.hair-loss", "Patchy hair loss", "skin"),
  s("skin.acne-vulgaris", "Persistent acne", "skin"),
  s("skin.eczema-flare", "Eczema flare", "skin"),
  s("skin.bruising-easy", "Easy bruising", "skin"),
  s("skin.dry-skin", "Severe dry skin / xerosis", "skin"),

  // ---------- Endocrine / Metabolic ----------
  s("endo.fatigue-generalized", "Generalized fatigue", "endo"),
  s("endo.hot-flashes", "Hot flashes / vasomotor instability", "endo"),
  s("endo.cold-intolerance", "Cold intolerance", "endo"),
  s("endo.heat-intolerance", "Heat intolerance", "endo"),
  s("endo.weight-gain", "Unexplained weight gain", "endo"),
  s("endo.weight-loss", "Unexplained weight loss", "endo"),
  s("endo.polyuria", "Polyuria (excessive urination)", "endo"),
  s("endo.polydipsia", "Polydipsia (excessive thirst)", "endo"),
  s("endo.hypoglycemia", "Hypoglycemic episode (shakiness, sweating, confusion)", "endo"),
  s("endo.hyperglycemia", "Hyperglycemic symptoms (thirst, blurred vision)", "endo"),
  s("endo.low-libido", "Low libido", "endo"),

  // ---------- Hematological / Immune ----------
  s("heme.frequent-infections", "Frequent infections", "heme"),
  s("heme.fever-recurrent", "Recurrent low-grade fevers", "heme"),
  s("heme.lymphadenopathy", "Swollen lymph nodes", "heme"),
  s("heme.night-sweats", "Night sweats", "heme"),
  s("heme.fatigue-anemia", "Fatigue with pallor (suggestive anemia)", "heme"),
  s("heme.allergic-rxn", "Allergic reaction / hives", "heme"),
];

// Common metadata vocabularies (used by the picker as multi-select chips).
export const TRIGGER_OPTIONS = [
  "Bending / lifting", "Sitting > 30 min", "Standing > 30 min", "Walking distance",
  "Climbing stairs", "Cold weather", "Hot weather", "Humidity", "Stress",
  "Loud noises", "Crowds", "Driving", "Sleep deprivation", "Skipped medication",
  "Caffeine", "Alcohol", "Spicy food", "Bright light", "Screen time",
];

export const RELIEF_OPTIONS = [
  "Rest", "Ice", "Heat", "Stretching", "OTC pain meds (Tylenol / NSAIDs)",
  "Prescription medication", "Sleep / nap", "Deep breathing", "Meditation",
  "Therapy techniques (CBT, grounding)", "Quiet dark room", "Brace / support",
  "TENS unit", "Massage", "Avoiding the trigger",
];

export const IMPACT_OPTIONS = [
  "Missed work", "Left work early", "Couldn't drive", "Needed help dressing",
  "Couldn't sleep through the night", "Cancelled social plans", "Skipped exercise",
  "Took unscheduled medication", "Visited ER / urgent care", "Couldn't care for kids",
  "Couldn't prepare meals", "Couldn't grocery shop", "Strained relationships",
];

// ---- Lookups -----------------------------------------------------------------

const BY_CODE = new Map(SYMPTOM_CATALOG.map((e) => [e.code, e]));
const BY_SYSTEM = SYMPTOM_CATALOG.reduce((acc, e) => {
  (acc[e.bodySystem] ||= []).push(e);
  return acc;
}, {});

const SYSTEM_LABEL = new Map(BODY_SYSTEMS.map((b) => [b.id, b.label]));

const INDEX = SYMPTOM_CATALOG.map((e) => ({
  code: e.code,
  label: e.label,
  bodySystem: e.bodySystem,
  bodyRegion: e.bodyRegion || "",
  painLike: !!e.painLike,
  blob: (e.label + " " + (SYSTEM_LABEL.get(e.bodySystem) || "") + " " + (e.bodyRegion || "")).toLowerCase(),
  starts: e.label.toLowerCase(),
}));

export function getSymptomByCode(code) {
  return BY_CODE.get(code) || null;
}

export function getSymptomsBySystem(systemId) {
  return BY_SYSTEM[systemId] || [];
}

export function getBodySystemLabel(id) {
  return SYSTEM_LABEL.get(id) || id;
}

/**
 * Search the catalog. Ranking: starts-with > word-starts-with > substring.
 * Returns up to `limit` results.
 */
export function findSymptomMatches(query, limit = 20) {
  const q = String(query || "").trim().toLowerCase();
  if (q.length < 2) return [];
  const out = [];
  for (const row of INDEX) {
    let rank = 99;
    if (row.starts.startsWith(q)) rank = 0;
    else if (row.blob.split(/\s+/).some((w) => w.startsWith(q))) rank = 1;
    else if (row.blob.includes(q)) rank = 2;
    else continue;
    out.push({ ...row, rank });
    if (out.length > 80) break;
  }
  out.sort((a, b) => (a.rank - b.rank) || a.label.localeCompare(b.label));
  return out.slice(0, limit);
}
